import type { CreateVehicleTO } from '@control-center/control-center-client-public-vehicles-ts';
import { zodResolver } from '@hookform/resolvers/zod';
import Button from 'components/actions/Button';
import FormDialogFooter from 'components/forms/FormDialogFooter';
import SingleSelect, { type SelectOption } from 'components/forms/SingleSelect';
import Dialog from 'components/overlays/Dialog';
import Tooltip from 'components/overlays/Tooltip';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { v4 as uuid } from 'uuid';
import { z } from 'zod';

import styles from './styles/CreateVehicleDialog.module.scss';
import { getVehicleTypeIdentifiers, type VehicleKind } from './systemVehicleUtils';

const PHYSICAL: VehicleKind = 'PHYSICAL';
const SIMULATOR: VehicleKind = 'SIMULATOR';
const HW_RIG: VehicleKind = 'HW_RIG';
const SIMULATED_CONNECTIVITY: VehicleKind = 'SIMULATED_CONNECTIVITY';

const vehicleTypeOptions: SelectOption[] = [
    { id: PHYSICAL, label: 'Physical' },
    { id: HW_RIG, label: 'HW rig' },
    { id: SIMULATOR, label: 'Simulated (agent)' },
    { id: SIMULATED_CONNECTIVITY, label: 'Simulated (connectivity)' },
];

// Non-physical vehicle kinds share the same flow (name + license plate only)
// and let the backend generate the VIN. They differ only in the type/subtype
// sent to the API (see getVehicleTypeIdentifiers).
const SIMPLE_VEHICLE_KINDS = new Set<VehicleKind>([SIMULATOR, HW_RIG, SIMULATED_CONNECTIVITY]);

const VIN_REGEX = /^[A-HJ-NPR-Z0-9]{17}$/;

const vehicleFormSchema = z
    .object({
        vehicleKind: z.string().min(1, 'Type is required'),
        // Ideally enforced by the backend schema, but VHS currently accepts any length for
        // name/licensePlate, so these caps are UI-only sanity guards.
        name: z.string().max(255, 'Cannot contain more than 255 characters'),
        licensePlate: z.string().max(50, 'Cannot contain more than 50 characters'),
        vin: z.string(),
        equipmentId: z.string(),
    })
    .superRefine((data, context) => {
        const isPhysical = data.vehicleKind === PHYSICAL;
        const isSimpleKind = SIMPLE_VEHICLE_KINDS.has(data.vehicleKind as VehicleKind);
        if (!isPhysical && !isSimpleKind) {
            return;
        }
        if (data.name.trim().length === 0) {
            context.addIssue({
                code: 'custom',
                path: ['name'],
                message: 'Vehicle name is required',
            });
        }
        if (!isPhysical) {
            return;
        }
        if (!VIN_REGEX.test(data.vin)) {
            context.addIssue({
                code: 'custom',
                path: ['vin'],
                message: 'Invalid VIN',
            });
        }
        if (!z.guid().safeParse(data.equipmentId.trim()).success) {
            context.addIssue({
                code: 'custom',
                path: ['equipmentId'],
                message: 'Invalid Equipment ID',
            });
        }
    });

type VehicleFormData = z.infer<typeof vehicleFormSchema>;

interface CreateVehicleDialogProps {
    show: boolean;
    isSubmitting: boolean;
    onCancel: () => void;
    onSubmit: (vehicle: CreateVehicleTO) => void | Promise<void>;
}

const isConflictError = (error: unknown): boolean =>
    typeof error === 'object' && error !== null && 'status' in error && error.status === 409;

const requiredLabel = (label: string) => (
    <>
        <Tooltip trigger={<span>{label}</span>} placement='top-start' variant='plain'>
            <p>Mandatory field</p>
        </Tooltip>
        <span className='text-color-danger'>*</span>
    </>
);

const FieldError = ({ message }: { message?: string }) =>
    message ? (
        <>
            <span className='form-control-feedback rioglyph rioglyph-error-sign top-27' />
            <span className='help-block'>
                <span>{message}</span>
            </span>
        </>
    ) : null;

const CreateVehicleDialog = ({ show, isSubmitting, onCancel, onSubmit }: CreateVehicleDialogProps) => {
    const {
        register,
        control,
        handleSubmit,
        watch,
        reset,
        formState: { errors, isValid, isDirty },
    } = useForm<VehicleFormData>({
        resolver: zodResolver(vehicleFormSchema),
        mode: 'onChange',
        defaultValues: {
            vehicleKind: '',
            name: '',
            licensePlate: '',
            vin: '',
            equipmentId: '',
        },
    });

    const [showExitConfirm, setShowExitConfirm] = useState(false);
    const [submitFailed, setSubmitFailed] = useState(false);
    const [vinAlreadyRegistered, setVinAlreadyRegistered] = useState(false);

    const selectedVehicleKind = watch('vehicleKind');
    const isPhysicalVehicle = selectedVehicleKind === PHYSICAL;
    const isSimpleVehicle = SIMPLE_VEHICLE_KINDS.has(selectedVehicleKind as VehicleKind);
    const hasVehicleFields = isPhysicalVehicle || isSimpleVehicle;

    const requestCancel = () => {
        if (isSubmitting) {
            return;
        }
        if (isDirty) {
            setShowExitConfirm(true);
        } else {
            onCancel();
        }
    };

    const confirmExit = () => {
        setShowExitConfirm(false);
        onCancel();
    };

    const dismissExit = () => {
        setShowExitConfirm(false);
    };

    useEffect(() => {
        if (!show) {
            reset();
            setShowExitConfirm(false);
            setSubmitFailed(false);
            setVinAlreadyRegistered(false);
        }
    }, [reset, show]);

    const submitVehicle = async (formData: VehicleFormData) => {
        const name = formData.name.trim();
        const license_plate = formData.licensePlate.trim() || null;
        const kind = formData.vehicleKind as VehicleKind;

        const payload: CreateVehicleTO = SIMPLE_VEHICLE_KINDS.has(kind)
            ? {
                  vin: null,
                  externalId: uuid(),
                  name,
                  license_plate,
                  ...getVehicleTypeIdentifiers(kind),
              }
            : {
                  vin: formData.vin.trim().toUpperCase(),
                  externalId: formData.equipmentId.trim(),
                  name,
                  license_plate,
                  ...getVehicleTypeIdentifiers(PHYSICAL),
              };

        setSubmitFailed(false);
        setVinAlreadyRegistered(false);
        try {
            await onSubmit(payload);
        } catch (error) {
            setSubmitFailed(true);
            setVinAlreadyRegistered(isConflictError(error));
        }
    };

    return (
        <>
            <Dialog
                data-testid='create-vehicle-dialog'
                show={show && !showExitConfirm}
                title={<p>Create new vehicle</p>}
                showCloseButton
                onClose={requestCancel}
                disableEsc={isSubmitting}
                body={
                    <form className={`${styles.form} font-body`} onSubmit={handleSubmit(submitVehicle)}>
                        <div className='display-flex flex-column gap-20 margin-bottom-20'>
                            <div className='display-flex flex-column gap-15 margin-bottom-20'>
                                <h5>Add vehicle information</h5>
                                <div
                                    className={`form-group mb-0 ${errors.vehicleKind ? 'has-error has-feedback' : ''}`}
                                >
                                    <label htmlFor='vehicle-type'>{requiredLabel('Type')}</label>
                                    <Controller
                                        control={control}
                                        name='vehicleKind'
                                        render={({ field }) => (
                                            <SingleSelect
                                                id='vehicle-type'
                                                name='vehicle-type'
                                                placeholder='Select type'
                                                options={vehicleTypeOptions}
                                                value={field.value}
                                                onChange={field.onChange}
                                                hasError={!!errors.vehicleKind}
                                            />
                                        )}
                                    />
                                    {errors.vehicleKind && (
                                        <span className='help-block'>{errors.vehicleKind.message}</span>
                                    )}
                                </div>

                                {hasVehicleFields && (
                                    <div className='display-flex flex-column gap-15'>
                                        <div
                                            className={`form-group mb-0 ${errors.name ? 'has-error has-feedback' : ''}`}
                                        >
                                            <label htmlFor='vehicle-name'>{requiredLabel('Vehicle name')}</label>
                                            <input
                                                id='vehicle-name'
                                                className='form-control font-body'
                                                placeholder='Start typing'
                                                autoComplete='off'
                                                aria-required
                                                aria-invalid={!!errors.name}
                                                {...register('name')}
                                            />
                                            <FieldError message={errors.name?.message} />
                                        </div>

                                        <div
                                            className={`form-group mb-0 ${errors.licensePlate ? 'has-error has-feedback' : ''}`}
                                        >
                                            <label htmlFor='vehicle-license-plate'>License plate</label>
                                            <input
                                                id='vehicle-license-plate'
                                                className='form-control font-body'
                                                placeholder='Start typing'
                                                autoComplete='off'
                                                aria-invalid={!!errors.licensePlate}
                                                {...register('licensePlate')}
                                            />
                                            <FieldError message={errors.licensePlate?.message} />
                                        </div>

                                        {isPhysicalVehicle && (
                                            <>
                                                <div
                                                    className={`form-group mb-0 ${errors.equipmentId ? 'has-error has-feedback' : ''}`}
                                                >
                                                    <label htmlFor='vehicle-equipment-id'>
                                                        {requiredLabel('Equipment ID')}
                                                    </label>
                                                    <input
                                                        id='vehicle-equipment-id'
                                                        className='form-control font-body'
                                                        placeholder='Start typing'
                                                        autoComplete='off'
                                                        aria-required
                                                        aria-invalid={!!errors.equipmentId}
                                                        {...register('equipmentId')}
                                                    />
                                                    <FieldError message={errors.equipmentId?.message} />
                                                </div>

                                                <div
                                                    className={`form-group mb-0 ${errors.vin || vinAlreadyRegistered ? 'has-error has-feedback' : ''}`}
                                                >
                                                    <label htmlFor='vehicle-vin'>{requiredLabel('VIN')}</label>
                                                    <input
                                                        id='vehicle-vin'
                                                        className='form-control font-body'
                                                        placeholder='Start typing'
                                                        autoComplete='off'
                                                        maxLength={17}
                                                        aria-required
                                                        aria-invalid={!!errors.vin || vinAlreadyRegistered}
                                                        {...register('vin')}
                                                    />
                                                    <FieldError
                                                        message={
                                                            vinAlreadyRegistered
                                                                ? 'VIN already registered'
                                                                : errors.vin?.message
                                                        }
                                                    />
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                            {submitFailed && (
                                <div className='display-flex justify-content-center margin-bottom-10'>
                                    <div
                                        className={`alert alert-danger display-grid width-350 rounded-md
                                            gap-10 grid-cols-2-max-content-1fr font-sans`}
                                        role='alert'
                                    >
                                        <div className='display-flex justify-content-start align-items-start'>
                                            <span className='text-color-danger text-size-h4 rioglyph rioglyph-info-sign' />
                                        </div>
                                        <div className='display-flex gap-10'>
                                            <div>
                                                <strong className='text-size-16 font-weight-600'>
                                                    Failed to create new vehicle
                                                </strong>
                                                <div className='margin-y-5 text-size-14'>
                                                    {vinAlreadyRegistered
                                                        ? 'Vehicle could not be created due to the VIN already being registered to another vehicle.'
                                                        : 'Vehicle could not be created due to a technical issue. Please try again or refresh the page. If the error persists contact the control center support team.'}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <FormDialogFooter
                            onCancel={requestCancel}
                            isValid={isValid}
                            isLoading={isSubmitting}
                            onConfirmLabel='Create new vehicle'
                        />
                    </form>
                }
            />
            <Dialog
                data-testid='create-vehicle-exit-confirm-dialog'
                show={showExitConfirm}
                title='Are you sure you want to cancel?'
                showCloseButton
                onClose={dismissExit}
                body={<p>Your changes will be lost and we won’t be able to recover your inputs.</p>}
                footer={
                    <div className='display-flex justify-content-end gap-10'>
                        <Button data-testid='create-vehicle-exit-confirm-back' onClick={dismissExit}>
                            No, back to editing
                        </Button>
                        <Button
                            className='btn btn-danger'
                            data-testid='create-vehicle-exit-confirm-cancel'
                            onClick={confirmExit}
                        >
                            Yes, cancel
                        </Button>
                    </div>
                }
            />
        </>
    );
};

export default CreateVehicleDialog;
